import { useState, type FC } from 'react'

import type { ValidationError } from '@skytech/auth'
import { Input, Select, Checkbox } from '@skytech/ui'
import { Button } from '@skytech/ui/Button'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { FieldProps } from 'formik'
import { Form, Field, Formik } from 'formik'
import * as yup from 'yup'

import { Translation } from '@/app/translations/Translation'
import { useTranslation } from '@/app/translations/translations-hooks'
import { useAdminAccess } from '@/hooks/access'
import useUser from '@/hooks/user'

import queries from '../queries'
import adminResources from '../resources'
import type { Department } from '../resources'

export const DepartmentModal: FC<{
  departmentDetails: Department | null
  onClose: () => void
  showAllCompanies: () => void
  showHidden: () => void
}> = ({ departmentDetails, onClose, showAllCompanies, showHidden }) => {
  useAdminAccess()
  const user = useUser()

  const translate = useTranslation()

  const [formikErrors, setFormikErrors] = useState<{ [key: string]: string }>({})

  const translatedMandatoryMessage = translate('This field is mandatory')

  const validationSchema = yup.object({
    name: yup.string().required(translatedMandatoryMessage),
    code: yup.string().required(translatedMandatoryMessage),
    userId: yup.number().required(translatedMandatoryMessage),
    customerId: yup.number().required(translatedMandatoryMessage),
    deputyUserId: yup.number().optional(),
    project: yup.string().optional(),
    inactive: yup.boolean().optional(),
    deputyMail: yup.boolean().optional(),
  })

  const { data: usersList, isLoading: isUsersListLoading } = useQuery(
    queries.adminUsersList(),
    adminResources.getUsersList
  )

  const usersOptions = usersList?.map((user: any) => ({
    value: user.id,
    label: user.firstName + ' ' + user?.lastName,
  }))

  const { data: customersList, isLoading: isCustomersListLoading } = useQuery(
    queries.adminCustomers(),
    adminResources.getCustomers
  )

  const customersOptions = customersList?.map(customer => ({
    value: customer.id,
    label: customer.name,
  }))

  const queryClient = useQueryClient()

  const updateMutation = useMutation(adminResources.updateDepartment, {
    onSuccess(department) {
      const list = queryClient.getQueryData(queries.adminDepartmentsList()) as Department[]
      const updatedAddresses = list.map(d => {
        if (d.id === department.id) {
          return department
        }
        return d
      })

      queryClient.setQueryData(queries.adminDepartmentsList(), [...updatedAddresses])

      if (department.customerId !== user?.customerId) {
        showAllCompanies()
      }

      if (department.inactive) {
        showHidden()
      }

      onClose()
    },
  })

  const createMutation = useMutation(adminResources.createDepartment, {
    onSuccess(department) {
      const list = queryClient.getQueryData(queries.adminDepartmentsList()) as Department[]

      queryClient.setQueryData(queries.adminDepartmentsList(), [department, ...list])
      if (department.customerId !== user?.customerId) {
        showAllCompanies()
      }

      if (department.inactive) {
        showHidden()
      }

      onClose()
    },
    onError(error: ValidationError) {
      // Status 422 is a validation error from server
      // Apart from standard validation we also check for number unique validation
      if (error?.status === 422 && error.json) {
        const formikErrors: Record<string, string> = {}
        Object.keys(error.json).forEach(key => {
          formikErrors[key] = error.json?.[key]?.join(', ')
        })
        setFormikErrors(formikErrors)
      }
    },
  })

  return (
    <Formik
      initialValues={{
        name: departmentDetails?.name || '',
        code: departmentDetails?.code || '',
        userId: departmentDetails?.userId || undefined,
        deputyUserId: departmentDetails?.deputyUserId || undefined,
        project: departmentDetails?.project || '',
        inactive: departmentDetails?.inactive || false,
        deputyMail: departmentDetails?.deputyMail || false,
        customerId: departmentDetails?.customerId || user?.customerId,
      }}
      validationSchema={validationSchema}
      onSubmit={formValues => {
        if (departmentDetails?.id) {
          updateMutation.mutate({
            id: departmentDetails.id,
            payload: formValues,
          })
        } else {
          createMutation.mutate({
            payload: formValues,
          })
        }
      }}
    >
      <div>
        <h2>
          <Translation id={departmentDetails ? 'Edit department' : 'Add department'} />
        </h2>
        <Form>
          {['admin', 'customer_head_admin'].includes(user?.role || '') && (
            <Field name="customerId">
              {({ field, form, meta }: FieldProps) => (
                <Select
                  id="customerId"
                  label={<Translation id="Company*" />}
                  options={customersOptions || []}
                  marginBottom={16}
                  {...field}
                  isLoading={isCustomersListLoading}
                  onChange={option => form.setFieldValue(field.name, option?.value)}
                  error={meta.error}
                  touched={meta.touched}
                />
              )}
            </Field>
          )}

          <Field name="name">
            {({ field, meta }: FieldProps) => (
              <Input
                isFullWidth
                id="name"
                label={<Translation id="Name*" />}
                marginBottom={16}
                error={meta.error}
                touched={meta.touched}
                {...field}
              />
            )}
          </Field>

          <Field name="code">
            {({ field, meta }: FieldProps) => (
              <Input
                isFullWidth
                id="code"
                label={<Translation id="Code*" />}
                marginBottom={16}
                error={meta.error || formikErrors?.code}
                touched={meta.touched}
                {...field}
              />
            )}
          </Field>

          <Field name="userId">
            {({ field, form, meta }: FieldProps) => (
              <Select
                id="userId"
                label={<Translation id="Head of Department*" />}
                options={usersOptions || []}
                marginBottom={16}
                {...field}
                isLoading={isUsersListLoading}
                onChange={option => form.setFieldValue(field.name, option?.value)}
                error={meta.error}
                touched={meta.touched}
              />
            )}
          </Field>

          <Field name="deputyUserId">
            {({ field, form }: FieldProps) => (
              <Select
                id="deputyUserId"
                label={<Translation id="Deputy" />}
                options={usersOptions || []}
                marginBottom={16}
                {...field}
                isLoading={isUsersListLoading}
                onChange={option => form.setFieldValue(field.name, option?.value)}
              />
            )}
          </Field>

          <Field name="deputyMail">
            {({ field, form }: FieldProps) => (
              <Checkbox
                marginBottom={16}
                id="deputyMail"
                isDisabled={!form.values.deputyUserId}
                marginTop={0}
                label={<Translation id="Deputy should also receive mail" />}
                checked={field.value}
                {...field}
                onChange={event => form.setFieldValue(field.name, event.target.checked)}
              />
            )}
          </Field>

          <Field name="project">
            {({ field, meta }: FieldProps) => (
              <Input
                isFullWidth
                id="project"
                label={<Translation id="Project" />}
                marginBottom={16}
                error={meta.error}
                touched={meta.touched}
                {...field}
              />
            )}
          </Field>

          <Field name="inactive">
            {({ field, form }: FieldProps) => (
              <Checkbox
                id="inactive"
                marginTop={0}
                label={<Translation id="Is department inactive" />}
                checked={field.value}
                {...field}
                onChange={event => form.setFieldValue(field.name, event.target.checked)}
              />
            )}
          </Field>

          <footer className="flex justify-end pt-2">
            <Button
              variant="tertiary"
              disabled={createMutation.isLoading || updateMutation.isLoading}
              onClick={onClose}
            >
              <Translation id="Cancel" />
            </Button>

            <Button
              variant="primary"
              type="submit"
              isLoading={createMutation.isLoading || updateMutation.isLoading}
            >
              <Translation id="Save" />
            </Button>
          </footer>
        </Form>
      </div>
    </Formik>
  )
}
